import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export interface InsurancePolicyDto {
  id: string;
  company_id: string;
  asset_id: string;
  policy_number: string;
  insurer_name: string | null;
  coverage_summary: string | null;
  premium_amount: number | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  notes: string | null;
  created_at?: string;
}

export interface InsuranceClaimDto {
  id: string;
  company_id: string;
  asset_id: string;
  policy_id: string | null;
  incident_date: string | null;
  description: string | null;
  claim_amount: number | null;
  status: string;
  amount_paid: number | null;
  notes: string | null;
  created_at?: string;
}

const assetInsuranceService = {
  async listPolicies(params?: { asset_id?: string }): Promise<InsurancePolicyDto[]> {
    const res = await api.get('/asset-insurance/policies', { params });
    const data = unwrap<InsurancePolicyDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getPolicy(id: string): Promise<InsurancePolicyDto | null> {
    const rows = await this.listPolicies();
    return rows.find((r) => r.id === id) ?? null;
  },
  async createPolicy(body: Record<string, unknown>): Promise<InsurancePolicyDto> {
    const res = await api.post('/asset-insurance/policies', body);
    return unwrap(res);
  },
  async updatePolicy(id: string, body: Record<string, unknown>): Promise<InsurancePolicyDto> {
    const res = await api.put(`/asset-insurance/policies/${id}`, body);
    return unwrap(res);
  },
  async removePolicy(id: string): Promise<void> {
    await api.delete(`/asset-insurance/policies/${id}`);
  },
  async listClaims(params?: { asset_id?: string }): Promise<InsuranceClaimDto[]> {
    const res = await api.get('/asset-insurance/claims', { params });
    const data = unwrap<InsuranceClaimDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getClaim(id: string): Promise<InsuranceClaimDto | null> {
    const rows = await this.listClaims();
    return rows.find((r) => r.id === id) ?? null;
  },
  async createClaim(body: Record<string, unknown>): Promise<InsuranceClaimDto> {
    const res = await api.post('/asset-insurance/claims', body);
    return unwrap(res);
  },
  async updateClaim(id: string, body: Record<string, unknown>): Promise<InsuranceClaimDto> {
    const res = await api.put(`/asset-insurance/claims/${id}`, body);
    return unwrap(res);
  },
  async removeClaim(id: string): Promise<void> {
    await api.delete(`/asset-insurance/claims/${id}`);
  },
};

export default assetInsuranceService;
